import React, { useEffect, useMemo, useState } from 'react';

/**
 * Shared modal for viewing/editing a single IDP.
 *
 * Props:
 *  - show: boolean
 *  - onClose: () => void
 *  - mode: 'owner' | 'manager'
 *  - initial: IDP object or null (if null + mode==='owner', we treat it as "create")
 *  - onSaveOwner: (payload, id?) => Promise<void>  // create (no id) or update (with id)
 *  - onSaveManager: (payload, id) => Promise<void>
 *  - onDelete?: (id) => Promise<void>              // only for owner
 *
 * IDP shape (server):
 * {
 *   id, contract_id,
 *   development_objective, competency_addressed,
 *   action_steps, success_measures, target_completion_date,
 *   manager_support, date_of_next_meeting
 * }
 */
export default function IdpViewModal({
    show,
    onClose,
    mode,
    initial,
    onSaveOwner,
    onSaveManager,
    onDelete,
    fetchOne, // new prop
}) {
    const isOwner = mode === 'owner';
    const isManager = mode === 'manager';
    const isCreate = isOwner && !initial?.id;

    const [form, setForm] = useState({
        development_objective: '',
        competency_addressed: '',
        action_steps: '',
        success_measures: '',
        target_completion_date: '',
        manager_support: '',
        date_of_next_meeting: '',
    });
    const [fetched, setFetched] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');

    useEffect(() => {
        let alive = true;
        setErr('');
        setFetched(null);
        if (!show) return;
        if (initial && initial.id && typeof fetchOne === 'function') {
            fetchOne(initial.id)
                .then((data) => {
                    if (!alive) return;
                    setFetched(data);
                    setForm({
                        development_objective: data.development_objective ?? '',
                        competency_addressed: data.competency_addressed ?? '',
                        action_steps: data.action_steps ?? '',
                        success_measures: data.success_measures ?? '',
                        target_completion_date: (data.target_completion_date ?? '').slice(0, 10),
                        manager_support: data.manager_support ?? '',
                        date_of_next_meeting: (data.date_of_next_meeting ?? '').slice(0, 10),
                    });
                })
                .catch(() => {
                    if (!alive) return;
                    // fallback to initial if fetch fails
                    setForm({
                        development_objective: initial.development_objective ?? '',
                        competency_addressed: initial.competency_addressed ?? '',
                        action_steps: initial.action_steps ?? '',
                        success_measures: initial.success_measures ?? '',
                        target_completion_date: (initial.target_completion_date ?? '').slice(0, 10),
                        manager_support: initial.manager_support ?? '',
                        date_of_next_meeting: (initial.date_of_next_meeting ?? '').slice(0, 10),
                    });
                });
        } else if (initial) {
            setForm({
                development_objective: initial.development_objective ?? '',
                competency_addressed: initial.competency_addressed ?? '',
                action_steps: initial.action_steps ?? '',
                success_measures: initial.success_measures ?? '',
                target_completion_date: (initial.target_completion_date ?? '').slice(0, 10),
                manager_support: initial.manager_support ?? '',
                date_of_next_meeting: (initial.date_of_next_meeting ?? '').slice(0, 10),
            });
        } else {
            // create template (owner)
            setForm({
                development_objective: '',
                competency_addressed: '',
                action_steps: '',
                success_measures: '',
                target_completion_date: '',
                manager_support: '',
                date_of_next_meeting: '',
            });
        }
        return () => { alive = false; };
    }, [show, initial, fetchOne]);

    const disableOwnerFields = isManager;
    const disableManagerFields = isOwner;

    const save = async (e) => {
        e?.preventDefault?.();
        setBusy(true); setErr('');
        try {
            if (isOwner) {
                // owner can create (no id) or update (has id)
                const payload = {
                    development_objective: form.development_objective,
                    competency_addressed: form.competency_addressed,
                    action_steps: form.action_steps || null,
                    success_measures: form.success_measures || null,
                    target_completion_date: form.target_completion_date || null,
                };
                await onSaveOwner(payload, initial?.id);
            } else {
                // manager can only update manager fields
                const payload = {
                    manager_support: form.manager_support || null,
                    date_of_next_meeting: form.date_of_next_meeting || null,
                };
                await onSaveManager(payload, initial?.id);
            }
            onClose();
        } catch (e) {
            setErr(e?.response?.data?.message || 'Failed to save.');
        } finally {
            setBusy(false);
        }
    };

    const doDelete = async () => {
        if (!onDelete || !initial?.id) return;
        if (!window.confirm('Delete this IDP?')) return;
        setBusy(true); setErr('');
        try {
            await onDelete(initial.id);
            onClose();
        } catch (e) {
            setErr(e?.response?.data?.message || 'Delete failed.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className={`modal ${show ? 'd-block show' : ''}`} tabIndex="-1" role="dialog" style={{ background: 'rgba(0,0,0,.4)' }}>
            <div className="modal-dialog modal-lg" role="document">
                <form className="modal-content" onSubmit={save}>
                    <div className="modal-header">
                        <h5 className="modal-title">
                            {isOwner ? (isCreate ? 'New Individual Development Plan' : 'Edit Individual Development Plan')
                                : 'View / Update IDP (Manager)'}
                        </h5>
                        <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
                    </div>

                    <div className="modal-body">
                        {err && <div className="alert alert-danger py-2">{err}</div>}

                        {/* Employee-owned fields */}
                        <div className="row g-3">
                            <div className="col-md-12">
                                <label className="form-label">Development Objective *</label>
                                <input
                                    className="form-control"
                                    value={form.development_objective}
                                    disabled={disableOwnerFields}
                                    onChange={(e) => setForm({ ...form, development_objective: e.target.value })}
                                    required={!disableOwnerFields}
                                />
                            </div>

                            <div className="col-md-12">
                                <label className="form-label">Competency Addressed *</label>
                                <input
                                    className="form-control"
                                    value={form.competency_addressed}
                                    disabled={disableOwnerFields}
                                    onChange={(e) => setForm({ ...form, competency_addressed: e.target.value })}
                                    required={!disableOwnerFields}
                                />
                            </div>

                            <div className="col-md-12">
                                <label className="form-label">Action Steps</label>
                                <textarea
                                    className="form-control"
                                    rows={3}
                                    value={form.action_steps}
                                    disabled={disableOwnerFields}
                                    onChange={(e) => setForm({ ...form, action_steps: e.target.value })}
                                />
                            </div>

                            <div className="col-md-12">
                                <label className="form-label">Success Measures</label>
                                <textarea
                                    className="form-control"
                                    rows={3}
                                    value={form.success_measures}
                                    disabled={disableOwnerFields}
                                    onChange={(e) => setForm({ ...form, success_measures: e.target.value })}
                                />
                            </div>

                            <div className="col-md-6">
                                <label className="form-label">Target Completion Date</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={form.target_completion_date}
                                    disabled={disableOwnerFields}
                                    onChange={(e) => setForm({ ...form, target_completion_date: e.target.value })}
                                />
                            </div>

                            {/* Manager fields */}
                            <div className="col-12"><hr className="my-2" /></div>

                            <div className="col-md-12">
                                <label className="form-label">Manager Support</label>
                                <textarea
                                    className="form-control"
                                    rows={3}
                                    value={form.manager_support}
                                    disabled={disableManagerFields}
                                    onChange={(e) => setForm({ ...form, manager_support: e.target.value })}
                                />
                            </div>

                            <div className="col-md-6">
                                <label className="form-label">Date of Next Meeting</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={form.date_of_next_meeting}
                                    disabled={disableManagerFields}
                                    onChange={(e) => setForm({ ...form, date_of_next_meeting: e.target.value })}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="modal-footer">
                        {isOwner && initial?.id && onDelete && (
                            <button type="button" className="btn btn-outline-danger me-auto" onClick={doDelete} disabled={busy}>
                                Delete
                            </button>
                        )}
                        <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={busy}>Close</button>
                        <button type="submit" className="btn btn-primary" disabled={busy}>
                            {busy ? 'Saving…' : isOwner ? (isCreate ? 'Create' : 'Save changes') : 'Save manager fields'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}